import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as StateLib from "@/Utils/StateLib";
import { GetTabularFieldDefs } from "@/Utils/DataLib";
import { appstr } from "@coreapp/utils/appstr";
import { useDirectoryTabularContext } from "@/providers/DirectoryTabularProvider";
import { TABLE_INITIAL_STATE } from "@coreapp/CoreApp/position-management.constants";
import { getLocalizationMap } from "@/Utils/Table/PosMgmtUtils";
import { filterInternalFieldDefs } from "@/Utils/FilterInternalFields";
import MultiClassFieldToggleMenu, {
  createCompositeFieldId
} from "@/appComponents/Blocks/Menus/FieldToggleMenu/MultiClassFieldToggleMenu";
import ColumnDisplayFieldTrigger from "../ColumnDisplayMenu/ColumnDisplayFieldTrigger";
import {
  enumObjClassNames,
  kSpanOfControl,
  kSubordinates,
  kHeadcount,
  kPeers,
  kPositionCount,
  kSubordinatePositions,
  kDirectSubordinatePositions,
  kPeerPositions,
  kPositionHoldersCount,
  kPositionAllocation,
  kBudgetedHeadcount,
  kSalaryRollup,
  kCurrentPositionCost,
  kPositionBudgetRollup
} from "@coreapp/constants";

const DirectoryColumnDisplayMenu = () => {
  const { state, setColumns, setLocalStorageCache } = useDirectoryTabularContext();
  const [localColumns, setLocalColumns] = useState(() => state.columns || undefined);
  const debounceTimerRef = useRef(null);

  const wsMgr = StateLib.GetWSMgr();
  const org = wsMgr.GetAccountName();
  const doc = StateLib.GetDoc();
  const chartID = doc.GetChartID();

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setLocalColumns(state.columns);
  }, [state.columns]);

  const [selectedColumnSet, selectedFieldIds] = useMemo(() => {
    const selectedFieldIds = (localColumns ?? []).map(fieldDef =>
      createCompositeFieldId(fieldDef.GetFieldID(), fieldDef.GetFieldClassName())
    );
    return [new Set(selectedFieldIds), selectedFieldIds];
  }, [localColumns]);

  const colFieldDefs = useMemo(() => {
    const fieldDefs = GetTabularFieldDefs();
    const isPositionChart = doc.IsPositionDocument();
    let allFields = filterInternalFieldDefs(Object.values(fieldDefs).flat());
    if (isPositionChart) {
      const employeeOnlyFields = [kSpanOfControl, kSubordinates, kHeadcount, kPeers];
      const positionOnlyFields = [
        kPositionCount,
        kSubordinatePositions,
        kDirectSubordinatePositions,
        kPeerPositions,
        kPositionHoldersCount,
        kPositionAllocation,
        kBudgetedHeadcount,
        kSalaryRollup,
        kCurrentPositionCost,
        kPositionBudgetRollup
      ];
      allFields = allFields.filter(fieldDef => {
        const className = fieldDef.GetFieldClassName();
        const fieldName = fieldDef.GetFieldName();
        if (className === enumObjClassNames.position && employeeOnlyFields.includes(fieldName))
          return false;
        if (className === enumObjClassNames.box && positionOnlyFields.includes(fieldName))
          return false;
        return true;
      });
    }
    return allFields;
  }, []);

  const handleChange = useCallback(
    target => {
      const targetFieldDef = target.fieldDef;
      const targetCompositeFieldId = target.value;

      const existingColumns = localColumns || [];

      const newColumnList = selectedColumnSet.has(targetCompositeFieldId)
        ? existingColumns.filter(
            fieldDef =>
              createCompositeFieldId(fieldDef.GetFieldID(), fieldDef.GetFieldClassName()) !==
              targetCompositeFieldId
          )
        : [...existingColumns, targetFieldDef];

      setLocalColumns(newColumnList);

      const selectedColumns = newColumnList.map(fieldDef => ({
        fieldID: fieldDef.GetFieldID(),
        objClassName: fieldDef.GetFieldClassName()
      }));
      setLocalStorageCache({ selectedColumns });

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        setColumns(newColumnList);
      }, 700);
    },
    [selectedColumnSet, localColumns, setColumns, setLocalStorageCache]
  );

  const localizationMap = getLocalizationMap();

  const disabledFieldIds = useMemo(() => {
    return Object.entries(TABLE_INITIAL_STATE.columnPinning.left).flatMap(
      ([objClassName, fieldId]) => createCompositeFieldId(fieldId, objClassName)
    );
  }, []);

  const getHoverTipContentForDisabledFields = useCallback(() => {
    return appstr("Core fields cannot be hidden in the table");
  }, []);

  return (
    <MultiClassFieldToggleMenu
      selectedFieldIds={selectedFieldIds}
      onChange={handleChange}
      trigger={<ColumnDisplayFieldTrigger dataTestId="directory-table-columns-menu-trigger" />}
      dataTestIdPrefix="directory-table-columns-menu-"
      fieldDefs={colFieldDefs}
      disabledFieldIds={disabledFieldIds}
      localizationMap={localizationMap}
      getHoverTipContent={getHoverTipContentForDisabledFields}
    />
  );
};

export default DirectoryColumnDisplayMenu;
